import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import heroHerbalWellness from '../../assets/images/hero-herbal-wellness.jpg';
import Icon from '../../components/common/Icon/Icon';
import { useLanguage } from '../../context/LanguageContext';
import { getProducts } from '../../services/catalogApi';

const categories = [
  { title: 'Herbal Capsules', form: 'capsules', icon: 'leaf', description: 'Browse capsule products by name, pack size and MRP.' },
  { title: 'Herbal Syrups', form: 'syrup', icon: 'bottle', description: 'Explore the available syrup range and compare product details.' },
  { title: 'Herbal Oil', form: 'oil', icon: 'drop', description: 'View the available herbal oil with its confirmed pack size and MRP.' }
];

const productForms = [
  { title: 'Syrups', form: 'syrup', icon: 'bottle', description: 'Browse syrup products and compare available pack sizes and prices.' },
  { title: 'Capsules', form: 'capsules', icon: 'leaf', description: 'Compare capsule product names, pack sizes and prices.' },
  { title: 'Herbal Oil', form: 'oil', icon: 'drop', description: 'Find the herbal oil listed in the Sanjeevi Grace Herbals catalogue.' }
];

const trustPoints = [
  ['leaf', 'Traditional Herbal Heritage', 'A brand rooted in traditional Siddha and herbal wellness knowledge.'],
  ['bottle', 'Clear Product Information', 'Product names, forms, pack sizes and MRPs are presented for easy comparison.'],
  ['drop', 'Quality and Purity Focus', 'A consistent brand commitment to quality, purity and responsible care.'],
  ['message', 'Accessible Customer Support', 'Help is available through phone, WhatsApp and professional email.']
];

const enquirySteps = [
  ['1', 'Browse the Catalogue', 'Search the complete range or filter products by form.'],
  ['2', 'Review Product Details', 'Compare the available pack sizes and maximum retail prices.'],
  ['3', 'Ask a Product Question', 'Use WhatsApp or customer support for product and ordering enquiries.'],
  ['4', 'Receive Personal Support', 'Our team responds during the published business hours.']
];

function HomePage() {
  const { translate } = useLanguage();
  const [publishedProducts, setPublishedProducts] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    getProducts({ limit: 48 }, controller.signal)
      .then(({ data }) => setPublishedProducts(Array.isArray(data) ? data : []))
      .catch((error) => {
        if (error.name !== 'AbortError') setPublishedProducts(null);
      });
    return () => controller.abort();
  }, []);

  const publishedCounts = useMemo(() => {
    if (!publishedProducts) return null;
    return publishedProducts.reduce((counts, product) => {
      counts[product.form] = (counts[product.form] || 0) + 1;
      return counts;
    }, {});
  }, [publishedProducts]);

  const catalogueHighlights = [
    ...(publishedProducts?.length ? [[String(publishedProducts.length), 'Products']] : []),
    ['3', 'Product forms'],
    ['Mon–Sat', 'Customer support']
  ];

  return (
    <main>
      <section className="hero">
        <div className="container hero__grid">
          <div className="hero__content">
            <p className="eyebrow">{translate('Traditional Siddha and Herbal Wellness')}</p>
            <h1>{translate('Reviving Health')} <em>{translate('Naturally.')}</em></h1>
            <p className="hero__lead">{translate('We bring traditional Siddha knowledge and modern quality standards together in an accessible herbal wellness experience.')}</p>
            <div className="hero__actions">
              <Link className="button button--primary" to="/shop">{translate('Explore the Shop')}</Link>
              <a className="button button--secondary" href="#why-choose-us">{translate('Why Choose Us')}</a>
            </div>
            <dl className="hero__highlights" aria-label="Catalogue highlights">
              {catalogueHighlights.map(([value, label]) => <div key={label}><dt>{value}</dt><dd>{translate(label)}</dd></div>)}
            </dl>
          </div>
          <div className="hero__visual" aria-label="Herbal wellness presentation">
            <span aria-hidden="true" className="hero__halo" />
            <div className="hero__image-frame">
              <img alt="Herbal wellness arrangement with green leaves and botanical oils" fetchPriority="high" height="960" src={heroHerbalWellness} width="960" />
            </div>
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="categories-title">
        <div className="container">
          <div className="section-heading">
            <div><p className="eyebrow">{translate('Browse by product form')}</p><h2 id="categories-title">{translate('Shop by Category')}</h2></div>
            <Link className="section-heading__link" to="/categories">{translate('View All Categories')} <span aria-hidden="true">→</span></Link>
          </div>
          <div className="category-grid">
            {categories.map((category) => (
              <Link className="category-card" key={category.form} to={`/shop?form=${category.form}`}>
                <span aria-hidden="true" className="category-card__icon"><Icon name={category.icon} /></span>
                <h3>{translate(category.title)}</h3>
                <p>{translate(category.description)}</p>
                <span className="category-card__action">{translate('Browse Category')} <span aria-hidden="true">→</span></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--tinted" aria-labelledby="product-range-title">
        <div className="container">
          <div className="section-heading">
            <div><p className="eyebrow">{translate('Our product range')}</p><h2 id="product-range-title">{translate('Explore the Catalogue')}</h2></div>
            <Link className="section-heading__link" to="/shop">{translate('Shop All Products')} <span aria-hidden="true">→</span></Link>
          </div>
          <div className="product-form-grid">
            {productForms.map((item) => (
              <article className="product-form-card" key={item.form}>
                <div className="product-form-card__top"><span aria-hidden="true" className="product-form-card__icon"><Icon name={item.icon} /></span>{publishedCounts?.[item.form] > 0 && <span className="product-form-card__count">{publishedCounts[item.form]} {translate('Products')}</span>}</div>
                <h3>{translate(item.title)}</h3>
                <p>{translate(item.description)}</p>
                <Link className="product-form-card__action" to={`/shop?form=${item.form}`}>{translate('View products')} <span aria-hidden="true">→</span></Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section" id="why-choose-us" aria-labelledby="trust-title">
        <div className="container">
          <div className="section-heading section-heading--centred"><div><p className="eyebrow">{translate('Why Choose Us')}</p><h2 id="trust-title">{translate('Wellness Rooted in Trust')}</h2></div></div>
          <div className="trust-grid">
            {trustPoints.map(([icon, title, description]) => <article className="trust-card" key={title}><span aria-hidden="true"><Icon name={icon} /></span><h3>{translate(title)}</h3><p>{translate(description)}</p></article>)}
          </div>
        </div>
      </section>

      <section className="section section--tinted brand-story" aria-labelledby="story-title">
        <div className="container story-grid">
          <div className="story-grid__content">
            <p className="eyebrow">{translate('About the brand')}</p>
            <h2 id="story-title">{translate('Heritage, Quality and Care')}</h2>
            <p>{translate("Sanjeevi Grace Herbals brings traditional Siddha and herbal wellness knowledge into a clear, approachable experience for today's customers.")}</p>
            <p>{translate('Our work is guided by respect for herbal heritage, a focus on quality and purity, and accessible support whenever customers need help.')}</p>
          </div>
          <aside className="principles-card" aria-labelledby="principles-title">
            <h3 id="principles-title">{translate('What We Stand For')}</h3>
            <ul><li>{translate('Traditional Siddha and herbal heritage')}</li><li>{translate('A focus on quality and purity')}</li><li>{translate('Clear product presentation')}</li><li>{translate('Support through phone, WhatsApp and email')}</li></ul>
          </aside>
        </div>
      </section>

      <section className="section" aria-labelledby="enquiry-steps-title">
        <div className="container">
          <div className="section-heading section-heading--centred"><div><p className="eyebrow">{translate('Simple and supported')}</p><h2 id="enquiry-steps-title">{translate('How to Find What You Need')}</h2></div></div>
          <ol className="order-steps">{enquirySteps.map(([number, title, description]) => <li key={number}><span aria-hidden="true">{number}</span><div><h3>{translate(title)}</h3><p>{translate(description)}</p></div></li>)}</ol>
        </div>
      </section>

      <section className="section section--tinted support-section" aria-labelledby="support-title">
        <div className="container support-card">
          <div className="support-card__primary">
            <p className="eyebrow">{translate('We are here to help')}</p><h2 id="support-title">{translate('Customer Support')}</h2>
            <p>{translate('Contact the Sanjeevi Grace Herbals support team Monday to Saturday between 10:00 AM and 5:00 PM for product and order enquiries.')}</p>
            <div className="support-details">
              <a href="tel:+917530037100"><span aria-hidden="true" className="support-details__icon"><Icon name="phone" /></span><span><strong>{translate('Call Customer Support')}</strong><small>+91 75300 37100</small></span><span aria-hidden="true">→</span></a>
              <a href="https://wa.me/917530037100" rel="noopener noreferrer" target="_blank"><span aria-hidden="true" className="support-details__icon"><Icon name="message" /></span><span><strong>{translate('Chat on WhatsApp')}</strong><small>+91 75300 37100</small></span><span aria-hidden="true">→</span></a>
              <a href="mailto:customersupport@sanjeevigraceherbals.com"><span aria-hidden="true" className="support-details__icon"><Icon name="mail" /></span><span><strong>{translate('Email Customer Support')}</strong><small>customersupport@sanjeevigraceherbals.com</small></span><span aria-hidden="true">→</span></a>
              <a href="mailto:complaints@sanjeevigraceherbals.com"><span aria-hidden="true" className="support-details__icon"><Icon name="mail" /></span><span><strong>{translate('Complaints and Escalations')}</strong><small>complaints@sanjeevigraceherbals.com</small></span><span aria-hidden="true">→</span></a>
            </div>
          </div>
          <aside className="support-card__secondary" aria-label="Support information">
            <div><span>{translate('Business Hours')}</span><strong>{translate('Monday–Saturday: 10:00 AM–5:00 PM')}</strong></div>
            <div><span>{translate('Response Time')}</span><strong>{translate('We usually respond during business hours.')}</strong></div>
            <div><span>{translate('Support Categories')}</span><strong>{translate('Product enquiries, orders, delivery, payments and returns')}</strong></div>
            <div><span>{translate('Business Address')}</span><strong>Door No. 69/4/1C/2, Vigneshwara Nagar, Thanthonimalai, Karur – 639005</strong></div>
          </aside>
        </div>
      </section>

      <section className="medical-note" aria-labelledby="medical-note-title">
        <div className="container"><h2 id="medical-note-title">{translate('Use products responsibly')}</h2><p>{translate('Website information is for general product-information purposes and does not replace professional medical advice, diagnosis or treatment.')}</p><Link to="/medical-disclaimer">{translate('Medical Disclaimer')}</Link></div>
      </section>
    </main>
  );
}

export default HomePage;
